import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FrazionamentoBox } from "./FrazionamentoBox";
import { Upload, Loader2, Building2, Trash2, RefreshCw, CheckCircle2, AlertTriangle, AlertCircle, FileUp, FileDown } from "lucide-react";
import { DateInputIT } from "@/components/DateInputIT";
import { fmtIT } from "@/lib/dateIT";

const euro = (v) => `€ ${Number(v || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function RiconciliazioneCompagnieTab({ meta, onChanged }) {
  const compagnie = meta?.compagnie_fc || [];
  const [compagnia, setCompagnia] = useState(compagnie[0]?.id || "generali");
  const [file, setFile] = useState(null);
  const [fileKey, setFileKey] = useState(0);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState("");
  const [batches, setBatches] = useState([]);
  const [ric, setRic] = useState(null);
  const [ricBatch, setRicBatch] = useState(null);
  const [creaSospesi, setCreaSospesi] = useState(true);
  const [ricDialog, setRicDialog] = useState(null); // {batch, dal, al}

  const loadBatches = useCallback(async () => {
    try { const { data } = await api.get("/pnc/fc-compagnie/batches"); setBatches(data.items || []); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { loadBatches(); }, [loadBatches]);

  const doPreview = async () => {
    if (!file) { toast.error("Seleziona un file"); return; }
    setBusy("preview"); setPreview(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/pnc/fc-compagnie/import?compagnia=${compagnia}&dry_run=true`, fd,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 });
      setPreview(data);
      toast.success(`Anteprima: ${data.righe_count} righe rilevate`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const doImport = async () => {
    if (!file) return;
    setBusy("import");
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/pnc/fc-compagnie/import?compagnia=${compagnia}&dry_run=false`, fd,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 });
      toast.success(`Importato: ${data.righe_count} righe · ${euro(data.totale_lordo)}`);
      setFile(null); setPreview(null); setFileKey((k) => k + 1); loadBatches();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const delBatch = async (id) => {
    if (!window.confirm("Eliminare questo Foglio Cassa compagnia? Verranno rimossi anche i sospesi generati dalla sua riconciliazione.")) return;
    try {
      const { data } = await api.delete(`/pnc/fc-compagnie/batches/${id}`);
      toast.success(`Eliminato${data.sospesi_rimossi ? ` · ${data.sospesi_rimossi} sospesi rimossi` : ""}`);
      if (ricBatch === id) { setRic(null); setRicBatch(null); }
      loadBatches(); onChanged && onChanged();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const riconcilia = async (b) => {
    setBusy(`ric-${b.id}`);
    try {
      const { data } = await api.post("/pnc/fc-compagnie/riconcilia", { batch_id: b.id, crea_sospesi: creaSospesi, dal: ricDialog?.dal || undefined, al: ricDialog?.al || undefined });
      setRic(data); setRicBatch(b.id); setRicDialog(null);
      const s = data.summary;
      toast.success(`Riconciliato: ${s.match} match, ${s.errore} errori, ${s.allarme} allarmi${s.sospesi_creati ? `, ${s.sospesi_creati} sospesi creati` : ""}`);
      loadBatches(); onChanged && onChanged();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const openRic = (b) => setRicDialog({ batch: b, dal: b.periodo_dal || "", al: b.periodo_al || "" });

  const riapriEsito = async (b) => {
    setBusy(`ap-${b.id}`);
    try {
      const { data } = await api.get("/pnc/fc-compagnie/riconciliazione", { params: { batch_id: b.id } });
      setRic(data); setRicBatch(b.id);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const exportEsitoPdf = async (bid) => {
    try {
      const { data: blob } = await api.get("/pnc/fc-compagnie/riconciliazione/pdf", { params: { batch_id: bid }, responseType: "blob" });
      window.open(URL.createObjectURL(blob), "_blank");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const reportDiscrepanze = async () => {
    try {
      const { data: blob } = await api.get("/pnc/fc-compagnie/discrepanze/pdf", { responseType: "blob" });
      window.open(URL.createObjectURL(blob), "_blank");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const compLabel = (id) => compagnie.find((c) => c.id === id)?.label || id;
  const compFmt = compagnie.find((c) => c.id === compagnia)?.formato || "";

  return (
    <div data-testid="fc-comp-tab">
      {/* Import */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 mb-5">
        <div className="flex items-center gap-2 text-slate-700 mb-3"><Building2 className="h-5 w-5 text-teal-600" /><h3 className="font-heading text-sm font-semibold">Importa Foglio Cassa della compagnia</h3></div>
        <div className="flex flex-wrap items-end gap-3">
          <div><Label className="text-xs text-slate-500">Compagnia</Label>
            <select value={compagnia} onChange={(e) => { setCompagnia(e.target.value); setPreview(null); }} className="h-10 rounded-md border border-slate-300 bg-white px-2 text-sm block w-56" data-testid="fc-comp-select">
              {compagnie.map((c) => <option key={c.id} value={c.id}>{c.label} · {c.formato}</option>)}
            </select></div>
          <div className="flex-1 min-w-[220px]"><Label className="text-xs text-slate-500">File ({compFmt})</Label>
            <input key={fileKey} type="file" accept=".xlsx,.xlsm,.pdf" onChange={(e) => { setFile(e.target.files[0]); setPreview(null); }} className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-teal-50 file:px-3 file:py-2 file:text-teal-700" data-testid="fc-comp-file" /></div>
          <Button onClick={doPreview} disabled={busy || !file} variant="outline" data-testid="fc-comp-preview-btn">{busy === "preview" ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FileUp className="h-4 w-4 mr-1" />} Anteprima</Button>
          <Button onClick={doImport} disabled={busy || !preview} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="fc-comp-import-btn">{busy === "import" ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />} Importa</Button>
        </div>
        {compagnie.find((c) => c.id === compagnia) && ["groupama", "essellepi", "altro"].includes(compagnia) && (
          <p className="text-[11px] text-slate-400 mt-2">I PDF di questa compagnia vengono letti automaticamente dal contenuto del documento (Groupama dal testo, Essellepi via OCR). Verifica sempre l'anteprima e il controllo "totale letto = totale dichiarato" prima di importare.</p>
        )}
        {preview && (
          <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm" data-testid="fc-comp-preview">
            <p><b>{preview.righe_count}</b> righe · Lordo {euro(preview.totale_lordo)} · Provvigioni {euro(preview.totale_provvigioni)}</p>
            {preview.totale_dichiarato != null && (
              preview.discrepanza ? (
                <div className="mt-2 flex items-start gap-2 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-red-700" data-testid="fc-comp-discrepanza-warning">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span className="text-xs">Attenzione: il totale letto ({euro(preview.totale_lordo)}) <b>non coincide</b> con il totale dichiarato dal Foglio Cassa ({euro(preview.totale_dichiarato)}), differenza {euro(preview.totale_discrepanza)}. L'estrazione potrebbe essere incompleta: verifica il file prima di importare.</span>
                </div>
              ) : (
                <div className="mt-2 flex items-center gap-2 rounded-md border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-emerald-700" data-testid="fc-comp-discrepanza-ok">
                  <CheckCircle2 className="h-4 w-4 shrink-0" />
                  <span className="text-xs">Totale letto coincide con il totale dichiarato dal Foglio Cassa ({euro(preview.totale_dichiarato)}): estrazione completa.</span>
                </div>
              )
            )}
            <div className="mt-2 max-h-48 overflow-y-auto">
              <Table><TableHeader><TableRow>
                <TableHead className="text-xs">Polizza</TableHead><TableHead className="text-xs">Contraente</TableHead>
                <TableHead className="text-xs">Descrizione</TableHead><TableHead className="text-xs text-right">Lordo</TableHead>
              </TableRow></TableHeader>
              <TableBody>{(preview.sample || []).map((r, i) => (
                <TableRow key={i}><TableCell className="text-xs">{r.polizza}</TableCell><TableCell className="text-xs">{r.contraente}</TableCell>
                <TableCell className="text-xs text-slate-500">{r.descrizione || "—"}</TableCell><TableCell className="text-xs text-right">{euro(r.lordo)}</TableCell></TableRow>
              ))}</TableBody></Table>
            </div>
          </div>
        )}
      </div>

      {/* Batches */}
      <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto mb-5">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <h3 className="font-heading text-sm font-semibold text-slate-700">Fogli Cassa compagnie importati</h3>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer" data-testid="fc-comp-crea-sospesi">
              <input type="checkbox" checked={creaSospesi} onChange={(e) => setCreaSospesi(e.target.checked)} /> Crea sospesi automatici dagli ALLARME
            </label>
            <Button size="sm" variant="outline" className="text-red-700 border-red-200" onClick={reportDiscrepanze} data-testid="fc-comp-discrepanze-btn" title="Elenco dei titoli presenti nei Fogli Cassa compagnie ma non ancora incassati in Prima Nota, per la riunione settimanale">
              <FileDown className="h-3.5 w-3.5 mr-1" /> Report discrepanze
            </Button>
          </div>
        </div>
        <Table>
          <TableHeader><TableRow>
            <TableHead>Compagnia</TableHead><TableHead>File</TableHead><TableHead className="text-right">Righe</TableHead>
            <TableHead className="text-right">Lordo</TableHead><TableHead>Stato</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {batches.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-8">Nessun Foglio Cassa compagnia importato.</TableCell></TableRow>
            ) : batches.map((b) => (
              <TableRow key={b.id} data-testid={`fc-comp-batch-${b.id}`} className="hover:bg-slate-50">
                <TableCell className="font-medium text-sm">{b.compagnia_label}</TableCell>
                <TableCell className="text-xs text-slate-500 max-w-[220px] truncate">{b.filename}</TableCell>
                <TableCell className="text-right text-sm">{b.righe_count}</TableCell>
                <TableCell className="text-right text-sm">{euro(b.totale_lordo)}</TableCell>
                <TableCell>
                  {b.riconciliazione ? (
                    <Badge className="bg-emerald-100 text-emerald-700 border-0 text-xs">
                      {b.riconciliazione.match} match · {b.riconciliazione.errore} err · {b.riconciliazione.allarme} all.
                    </Badge>
                  ) : <Badge variant="outline" className="text-xs text-slate-500">Importato</Badge>}
                </TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button size="sm" variant="outline" disabled={busy === `ric-${b.id}`} onClick={() => openRic(b)} data-testid={`fc-comp-ric-${b.id}`}>
                    {busy === `ric-${b.id}` ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />} Riconcilia
                  </Button>
                  {b.riconciliazione && (
                    <Button size="sm" variant="ghost" className="ml-1 text-teal-700" disabled={busy === `ap-${b.id}`} onClick={() => riapriEsito(b)} data-testid={`fc-comp-riapri-${b.id}`} title="Riapri l'ultimo esito salvato">
                      {busy === `ap-${b.id}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Riapri"}
                    </Button>
                  )}
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500 ml-1" onClick={() => delBatch(b.id)} data-testid={`fc-comp-del-${b.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Esiti riconciliazione */}
      {ric && (
        <div className="space-y-4" data-testid="fc-comp-esiti">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h3 className="font-heading text-sm font-semibold text-slate-700">Esito riconciliazione{ric.compagnia_label ? ` — ${ric.compagnia_label}` : ""}{ric.summary?.dal ? ` · ${fmtIT(ric.summary.dal)} → ${fmtIT(ric.summary.al)}` : ""}</h3>
            <Button size="sm" variant="outline" onClick={() => exportEsitoPdf(ricBatch)} data-testid="fc-comp-esiti-pdf"><FileDown className="h-4 w-4 mr-1" /> Esporta PDF</Button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="rounded-xl border border-slate-200 bg-white p-3"><div className="text-[11px] uppercase text-slate-400">Righe FC</div><div className="text-lg font-bold">{ric.summary.n_fc}</div></div>
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3"><div className="text-[11px] uppercase text-emerald-600 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Match</div><div className="text-lg font-bold text-emerald-700">{ric.summary.match}</div></div>
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3"><div className="text-[11px] uppercase text-amber-600 flex items-center gap-1"><AlertCircle className="h-3 w-3" /> Errori</div><div className="text-lg font-bold text-amber-700">{ric.summary.errore}</div></div>
            <div className="rounded-xl border border-red-200 bg-red-50 p-3"><div className="text-[11px] uppercase text-red-600 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Allarmi{ric.summary.sospesi_creati ? ` · ${ric.summary.sospesi_creati} sospesi` : ""}</div><div className="text-lg font-bold text-red-700">{ric.summary.allarme}</div></div>
          </div>

          {ric.errore.length > 0 && (
            <EsitoTable title="ERRORE — incassati in Prima Nota ma assenti nel Foglio Cassa compagnia" color="amber" rows={ric.errore}
              cols={[["polizza", "Polizza"], ["cliente", "Cliente"], ["entrata", "Entrata", true], ["data", "Data"]]} testid="fc-comp-errore" />
          )}
          {ric.allarme.length > 0 && (
            <EsitoTable title="ALLARME — presenti nel Foglio Cassa compagnia ma assenti in Prima Nota" color="red" rows={ric.allarme}
              cols={[["polizza", "Polizza"], ["contraente", "Contraente"], ["descrizione", "Descrizione"], ["lordo", "Lordo", true]]} testid="fc-comp-allarme" />
          )}
          {ric.match.length > 0 && (
            <EsitoTable title="MATCH — corrispondenze trovate" color="emerald" rows={ric.match}
              cols={[["polizza", "Polizza"], ["contraente", "Contraente"], ["lordo", "Lordo FC", true], ["pnc_entrata", "Entrata PN", true], ["pnc_data", "Data incasso PN"], ["criterio", "Criterio"], ["diff", "Diff.", true]]} testid="fc-comp-match" />
          )}
        </div>
      )}

      <div className="mt-5"><FrazionamentoBox /></div>

      {/* Dialog periodo riconciliazione */}
      <Dialog open={!!ricDialog} onOpenChange={(o) => !o && setRicDialog(null)}>
        <DialogContent className="max-w-md" data-testid="fc-comp-ric-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Riconcilia {ricDialog?.batch?.compagnia_label}</DialogTitle>
            <DialogDescription>Il periodo è precompilato con le date del Foglio Cassa caricato. Gli "ERRORE" (incassi in Prima Nota assenti nel Foglio Cassa) vengono cercati solo in questo intervallo, così non compaiono falsi errori dallo storico.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <div><Label className="text-xs text-slate-500">Dal</Label><DateInputIT value={ricDialog?.dal || ""} onChange={(v) => setRicDialog((d) => ({ ...d, dal: v }))} className="bg-white" testid="fc-comp-ric-dal" /></div>
            <div><Label className="text-xs text-slate-500">Al</Label><DateInputIT value={ricDialog?.al || ""} onChange={(v) => setRicDialog((d) => ({ ...d, al: v }))} className="bg-white" testid="fc-comp-ric-al" /></div>
          </div>
          <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
            <input type="checkbox" checked={creaSospesi} onChange={(e) => setCreaSospesi(e.target.checked)} data-testid="fc-comp-ric-crea-sospesi" /> Crea sospesi automatici dagli ALLARME
          </label>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRicDialog(null)}>Annulla</Button>
            <Button className="bg-teal-600 hover:bg-teal-700 text-white" disabled={busy.startsWith("ric-")} onClick={() => riconcilia(ricDialog.batch)} data-testid="fc-comp-ric-run">
              {busy.startsWith("ric-") ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />} Avvia riconciliazione
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}

function EsitoTable({ title, color, rows, cols, testid }) {
  const head = { red: "text-red-700 bg-red-50", amber: "text-amber-700 bg-amber-50", emerald: "text-emerald-700 bg-emerald-50" }[color];
  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid={testid}>
      <div className={`px-4 py-2 text-xs font-semibold ${head}`}>{title} · {rows.length}</div>
      <div className="overflow-x-auto max-h-72 overflow-y-auto">
        <Table>
          <TableHeader><TableRow>{cols.map((c) => <TableHead key={c[0]} className={`text-xs ${c[2] ? "text-right" : ""}`}>{c[1]}</TableHead>)}</TableRow></TableHeader>
          <TableBody>{rows.map((r, i) => (
            <TableRow key={i}>{cols.map((c) => (
              <TableCell key={c[0]} className={`text-xs ${c[2] ? "text-right" : ""}`}>
                {c[2] ? `€ ${Number(r[c[0]] || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : /data/.test(c[0]) ? fmtIT(r[c[0]]) : (r[c[0]] || "—")}
              </TableCell>
            ))}</TableRow>
          ))}</TableBody>
        </Table>
      </div>
    </div>
  );
}
